import React, { useEffect, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap"
import Swal from "sweetalert2"
import { variable_ids } from "utils/Data"

const StaffModal = ({
  isOpen,
  toggle,
  staffData,
  roles,
  staffList,
  onChange,
  onSave,
  setStaffData,
}) => {
  const [appAccessSwitch, setAppAccessSwitch] = useState(false)
  const [adminSwitchOn, setAdminSwitchOn] = useState(false)
  const [isWindowCleaner, setisWindowCleaner] = useState(false)
  const [isIndustrialCleaner, setisIndustrialCleaner] = useState(false)

  useEffect(() => {
    if (staffData) {
      setAppAccessSwitch(staffData?.appLogin)
      setAdminSwitchOn(staffData?.adminPanelAccess)
      setisWindowCleaner(staffData?.isWindowCleaner)
      setisIndustrialCleaner(staffData?.isIndustrialCleaner)
    }
  }, [staffData])

  const handleAdminSwitch = e => {
    e.preventDefault()
    const isCurrentlyOn = adminSwitchOn
    Swal.fire({
      title: "Are you sure?",
      text: isCurrentlyOn
        ? "Do you want to disable Admin Panel Access?"
        : "Do you want to enable Admin Panel Access?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isCurrentlyOn ? "Yes, disable" : "Yes, enable",
      cancelButtonText: "No, cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setAdminSwitchOn(!isCurrentlyOn)
        setStaffData(prev => ({ ...prev, adminPanelAccess: !isCurrentlyOn }))
      }
    })
  }

  const handleCleanerSwitch = type => {
    const isWindow = type === "window"
    const isCurrentlyOn = isWindow ? isWindowCleaner : isIndustrialCleaner

    Swal.fire({
      title: "Are you sure?",
      text: isCurrentlyOn
        ? `Do you want to remove this employee as ${
            isWindow ? "Window" : "Industrial"
          } Cleaner?`
        : `Do you want to mark this employee as ${
            isWindow ? "Window" : "Industrial"
          } Cleaner?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isCurrentlyOn ? "Yes, Remove" : "Yes, Mark",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (!result.isConfirmed) return

      const newWindowValue = isWindow ? !isCurrentlyOn : false
      const newIndustrialValue = !isWindow ? !isCurrentlyOn : false

      const updated = {
        ...staffData,
        isWindowCleaner: newWindowValue,
        isIndustrialCleaner: newIndustrialValue,
      }

      setisWindowCleaner(newWindowValue)
      setisIndustrialCleaner(newIndustrialValue)
      setStaffData(updated)
    })
  }

  const handleAppAccessSwitch = e => {
    e.preventDefault()
    const isCurrentlyOn = appAccessSwitch
    Swal.fire({
      title: "Are you sure?",
      text: isCurrentlyOn
        ? "Do you want to disable Mobile Application Access?"
        : "Do you want to enable Mobile Application Access?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isCurrentlyOn ? "Yes, disable" : "Yes, enable",
      cancelButtonText: "No, cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setAppAccessSwitch(!isCurrentlyOn)
        setStaffData(prev => ({ ...prev, appLogin: !isCurrentlyOn }))
      }
    })
  }

  const validateForm = () => {
    const requiredFields = [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "position", label: "Position Title" },
      { key: "roleId", label: "Role" },
      { key: "startDate", label: "Start Date" },
      { key: "hourlyRate", label: "Hourly Rate" },
      { key: "type", label: "Employee Type" },
      { key: "paymentFrequency", label: "Payroll Frequency" },
      { key: "address1", label: "Address 1" },
      { key: "address2", label: "Address 2" },
      { key: "city", label: "City" },
      { key: "country", label: "Country" },
      { key: "postcode", label: "Postcode" },
    ]

    const missingFields = requiredFields
      .filter(field => {
        const value = staffData?.[field.key]
        return !value || (typeof value === "string" && value.trim() === "")
      })
      .map(field => field.label)

    if (missingFields.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Required Fields",
        html: `Please fill in the following required fields:<br/><strong>${missingFields.join(
          ", "
        )}</strong>`,
      })
      return false
    }

    // Additional validation for email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(staffData.email)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Email",
        text: "Please enter a valid email address.",
      })
      return false
    }

    // Validate phone starts with +44 and has reasonable length
    if (
      !staffData.phone ||
      !staffData.phone.startsWith("+44") ||
      staffData.phone.replace(/\D/g, "").length < 12
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Phone",
        text: "Please enter a valid UK phone number (e.g., +44 7123456789).",
      })
      return false
    }

    if (staffData?.visaType !== "" && !staffData?.visaExpiry) {
      Swal.fire({
        icon: "warning",
        title: "Visa Expiry date is required",
      })
      return false
    }

    // Validate hourly rate is positive
    if (parseFloat(staffData.hourlyRate) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Hourly Rate",
        text: "Hourly rate must be greater than 0.",
      })
      return false
    }

    return true
  }

  const handleSave = () => {
    if (validateForm()) {
      onSave()
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
      <ModalHeader toggle={toggle}>
        {staffData?.id ? "Edit Employee" : "Add Employee"}
      </ModalHeader>
      <ModalBody>
        <Form>
          {/* Row 1 */}
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>
                  Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="name"
                  value={staffData?.name || ""}
                  onChange={onChange}
                  placeholder="Enter full name"
                  invalid={!staffData?.name?.trim()}
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>
                  Email <span className="text-danger">*</span>
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={staffData?.email || ""}
                  onChange={onChange}
                  placeholder="Enter email"
                  invalid={!staffData?.email?.trim()}
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>
                  Phone <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="phone"
                  value={
                    staffData?.phone
                      ? staffData.phone.startsWith("+44")
                        ? staffData.phone
                        : `+44 ${staffData.phone}`
                      : "+44 "
                  }
                  onChange={e => {
                    let inputValue = e.target.value.replace(/\D/g, "")
                    if (inputValue.startsWith("44"))
                      inputValue = inputValue.slice(2)
                    if (inputValue.startsWith("0"))
                      inputValue = inputValue.slice(1)

                    onChange({
                      target: { name: "phone", value: `+44 ${inputValue}` },
                    })
                  }}
                  maxLength="15"
                  placeholder="+44 XXXXXXXXXX"
                  invalid={!staffData?.phone?.startsWith("+44")}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Row 2 */}
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>
                  Position Title <span className="text-danger">*</span>
                </Label>
                <select
                  className="form-select rounded-5 px-3"
                  name="position"
                  value={staffData?.position || ""}
                  onChange={onChange}
                >
                  <option value="">Select Designation</option>
                  <option value="cleaner">Cleaner</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>
                  Role <span className="text-danger">*</span>
                </Label>
                <select
                  className="form-select rounded-5 px-3"
                  name="roleId"
                  value={staffData?.roleId || ""}
                  onChange={onChange}
                >
                  <option value="">Select Role</option>
                  {roles?.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.roleName}
                    </option>
                  ))}
                </select>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>
                  Start Date <span className="text-danger">*</span>
                </Label>
                <Input
                  type="date"
                  name="startDate"
                  value={staffData?.startDate || ""}
                  onChange={onChange}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Row 3 */}
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>
                  Hourly Rate (£) <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  name="hourlyRate"
                  value={staffData?.hourlyRate || ""}
                  onChange={onChange}
                  placeholder="Enter salary per hour"
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>Manager</Label>
                <select
                  className="form-select rounded-5 px-3"
                  name="managerId"
                  value={staffData?.managerId || ""}
                  onChange={onChange}
                >
                  <option value="">Select Manager</option>
                  {staffList
                    .filter(item => item.position === "manager")
                    .map(staff => (
                      <option key={staff.id} value={staff.id}>
                        {staff.name}
                      </option>
                    ))}
                </select>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>
                  Employee Type <span className="text-danger">*</span>
                </Label>
                <select
                  className="form-select rounded-5 px-3"
                  name="type"
                  value={staffData?.type || ""}
                  onChange={onChange}
                >
                  <option value="">Select Employee Type</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Zero-hour contract">Zero-hour contract</option>
                </select>
              </FormGroup>
            </Col>
          </Row>

          {/* Row 4 */}
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Supervisor</Label>
                <select
                  className="form-select rounded-5 px-3"
                  name="supervisorId"
                  value={staffData?.supervisorId || ""}
                  onChange={onChange}
                >
                  <option value="">Select Supervisor</option>
                  {staffList
                    .filter(item => item.position === "supervisor")
                    .map(staff => (
                      <option key={staff.id} value={staff.id}>
                        {staff.name}
                      </option>
                    ))}
                </select>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>
                  Payroll Frequency <span className="text-danger">*</span>
                </Label>
                <select
                  className="form-select rounded-5 px-3"
                  name="paymentFrequency"
                  value={staffData?.paymentFrequency || ""}
                  onChange={onChange}
                >
                  <option value="">Select Payroll Frequency</option>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </FormGroup>
            </Col>

            <Col md={2}>
              <FormGroup>
                <Label>Employee App Login</Label>
                <div className="form-check form-switch">
                  <Input
                    type="switch"
                    checked={appAccessSwitch}
                    onChange={handleAppAccessSwitch}
                  />
                </div>
              </FormGroup>
            </Col>

            <Col md={2}>
              <FormGroup>
                <Label>Admin Panel Access</Label>
                <div className="form-check form-switch">
                  <Input
                    type="switch"
                    checked={adminSwitchOn}
                    onChange={handleAdminSwitch}
                  />
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Visa Type</Label>
                <select
                  className="form-select rounded-5 px-3"
                  name="visaType"
                  value={staffData?.visaType || ""}
                  onChange={onChange}
                >
                  <option value="">Select Visa Type</option>
                  <option value="student">Student Visa</option>
                  <option value="other">Other Visa</option>
                </select>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>
                  Visa Expiry{" "}
                  {staffData?.visaType !== "" && (
                    <span className="text-danger">*</span>
                  )}
                </Label>
                <input
                  type="date"
                  className="form-control rounded-5 px-3"
                  name="visaExpiry"
                  value={staffData?.visaExpiry || ""}
                  onChange={onChange}
                  min={new Date().toISOString().split("T")[0]}
                />
              </FormGroup>
            </Col>
            {staffData?.position === "cleaner" ? (
              <>
                <Col md={2}>
                  <FormGroup>
                    <Label>Window Cleaner</Label>
                    <div className="form-check form-switch">
                      <Input
                        type="switch"
                        checked={isWindowCleaner}
                        onChange={() => handleCleanerSwitch("window")}
                      />
                    </div>
                  </FormGroup>
                </Col>

                <Col md={2}>
                  <FormGroup>
                    <Label>Industrial Cleaner</Label>
                    <div className="form-check form-switch">
                      <Input
                        type="switch"
                        checked={isIndustrialCleaner}
                        onChange={() => handleCleanerSwitch("industrial")}
                      />
                    </div>
                  </FormGroup>
                </Col>
              </>
            ) : (
              <Col md={4}>
                <FormGroup>
                  <Label>Admin Staff</Label>
                  <div className="form-check form-switch">
                    <Input
                      type="switch"
                      name="adminStaff"
                      checked={staffData?.adminStaff}
                      onChange={e => {
                        setStaffData(prev => ({
                          ...prev,
                          adminStaff: e.target.checked,
                        }))
                      }}
                    />
                  </div>
                </FormGroup>
              </Col>
            )}
          </Row>

          {/* Row 6 */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Address 1 <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  name="address1"
                  rows="2"
                  value={staffData?.address1 || ""}
                  onChange={onChange}
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>
                  Address 2 <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  name="address2"
                  rows="2"
                  value={staffData?.address2 || ""}
                  onChange={onChange}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Row 5 */}
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>
                  City <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="city"
                  value={staffData?.city || ""}
                  onChange={onChange}
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>
                  Country <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="country"
                  value={staffData?.country || ""}
                  onChange={onChange}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>
                  Postcode <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="postcode"
                  value={staffData?.postcode || ""}
                  onChange={onChange}
                />
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button className="btn-blue" onClick={handleSave}>
          {staffData?.id ? "Update" : "Save"}
        </Button>
        <Button className="btn-cancel" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default StaffModal
